import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

// Les RPC de liaison parent (migration 0150) sont postérieures aux types
// générés (volontairement stale, cf. CLAUDE.md). On passe par un client
// générique, comme apps/app/src/lib/exercise-dag/api.ts.
const db = supabase as unknown as SupabaseClient;

export type AcceptParentLinkResult = {
  /** id du compte que l'on vient de relier (l'autre partie) */
  linked_id: string;
  linked_display_name: string;
  linked_role: 'parent' | 'student' | 'tutor' | 'admin';
};

/** Invalide les listes des deux côtés du lien (on ne sait pas quel rôle agit). */
function invalidateParentLinks(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['parent-children'] });
  qc.invalidateQueries({ queryKey: ['my-parents'] });
}

/** Récupère (ou crée) le code de liaison parent ⇄ enfant du compte courant. */
export function useEnsureParentLinkCode(userId: string) {
  return useQuery({
    queryKey: ['parent-link-code', userId],
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<string> => {
      const { data, error } = await db.rpc('ensure_parent_link_code');
      if (error) throw new Error(error.message);
      return data as string;
    },
  });
}

/** Active le lien parent ⇄ enfant via le code partagé par l'autre partie. */
export function useAcceptParentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string): Promise<AcceptParentLinkResult> => {
      const { data, error } = await db.rpc('accept_parent_link', { p_code: code.trim() });
      if (error) throw new Error(error.message);
      const row = (data as AcceptParentLinkResult[])?.[0];
      if (!row) throw new Error('Réponse inattendue du serveur.');
      return row;
    },
    onSuccess: () => invalidateParentLinks(qc),
  });
}

/** Coupe un lien parent ⇄ enfant existant (depuis l'un ou l'autre des comptes). */
export function useRevokeParentLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (otherId: string) => {
      const { error } = await db.rpc('revoke_parent_link', { p_other_id: otherId });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateParentLinks(qc),
  });
}
